/**
 * DOIS CADASTROS DA MESMA FAZENDA.
 *
 * A caça na base de 26 clientes derrubou o sinal que qualquer um usaria primeiro:
 *
 *   MESMO CONTATO NÃO É DUPLICATA. "GABRIEL JUNQUEIRA" é o contato de CINCO
 *   cadastros — ADILSON, ALEXANDRE, JULINHO, LUCAS e o dele próprio — e
 *   "NEZINHO" é o contato de três. Ali o contato é o intermediário que traz o
 *   serviço, e o cliente é o dono da fazenda. Avisar por contato igual acusaria
 *   oito cadastros legítimos.
 *
 *   TELEFONE ESTÁ VAZIO em 25 dos 26. O sinal mais forte de qualquer sistema de
 *   cadastro é inerte aqui. Fica na regra porque quando começarem a preencher ele
 *   passa a valer sozinho, e não custa nada.
 *
 *   SOBRENOME IGUAL É COINCIDÊNCIA. "BRENO JUNQUEIRA" e "GABRIEL JUNQUEIRA" são
 *   duas pessoas, e qualquer semelhança difusa de texto os juntaria.
 *
 * O que sobra, e basta: O NOME, normalizado. Acento, caixa, pontuação e espaço
 * repetido não mudam a fazenda, e são exatamente por onde o recadastro entra —
 * "Fazenda Santa Rita" digitada de novo como "FAZENDA SANTA RITA".
 *
 * E uma segunda camada: NOME CONTIDO NO OUTRO. Quem cadastra "FAZENDA DA LUZ"
 * onde já existe "FAZENDA DA LUZ (CLEVERSON LIMA NEVES)" está recadastrando, e
 * quem cadastra "LUCAS SILVA" onde existe "LUCAS" provavelmente também. As
 * palavras genéricas saem da conta antes, senão toda fazenda seria parecida com
 * toda fazenda.
 */

/** Tira acento, pontuação, caixa e espaço repetido. O que sobra é o nome. */
const normaliza = (v) => String(v ?? '')
  .normalize('NFD')
  .replace(/[̀-ͯ]/g, '')
  .toUpperCase()
  .replace(/[^A-Z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

/**
 * O telefone comparável: só dígitos, sem o país e sem o zero de operadora.
 *
 * O único telefone da base está gravado como "+55 22 99242-3125", treze dígitos.
 * Quem digitar o mesmo número como "22 99242-3125" escreve onze. Comparar os
 * dígitos crus faria os dois passarem por telefones diferentes — e é exatamente
 * assim que a duplicata entra, um cadastro com o código do país e outro sem.
 *
 * Tira o "55" da frente só quando o que sobra tem tamanho de telefone brasileiro,
 * dez ou onze dígitos: assim um número que por acaso comece com 55 não perde os
 * dois primeiros dígitos.
 */
const soDigitos = (v) => {
  let d = String(v ?? '').replace(/\D/g, '');
  if (d.startsWith('0') && d.length > 10) d = d.slice(1);
  if (d.startsWith('55') && (d.length === 12 || d.length === 13)) d = d.slice(2);
  return d;
};

/**
 * Palavras que não distinguem uma fazenda de outra.
 *
 * Sem tirá-las, "Fazenda Boa Vista" e "Fazenda Santa Rita" compartilhariam a
 * palavra FAZENDA e a regra de contenção viraria um alarme permanente.
 */
const GENERICAS = new Set([
  'FAZENDA', 'FAZ', 'SITIO', 'CHACARA', 'ESTANCIA', 'HARAS', 'AGROPECUARIA',
  'AGRO', 'RANCHO', 'DO', 'DA', 'DE', 'DOS', 'DAS', 'E', 'LTDA', 'ME', 'EIRELI',
]);

const significativas = (nome) => new Set(
  normaliza(nome).split(' ').filter(p => p.length > 1 && !GENERICAS.has(p)));

const contido = (a, b) => {
  if (a.size === 0 || b.size === 0) return false;
  if (a.size >= b.size) return false;
  return [...a].every(p => b.has(p));
};

/** Por que este cadastro parece com o outro, em ordem de força. */
export const MOTIVO_DA_SEMELHANCA = {
  mesmo_nome: 'já existe um cliente com este nome',
  mesmo_telefone: 'já existe um cliente com este telefone',
  nome_contido: 'o nome é parte do nome de um cliente que já existe',
};

/**
 * Cadastros parecidos com este, com o motivo de cada um.
 *
 * `cliente` pode ser o formulário ainda não salvo — precisa de `name` e, se
 * houver, `phone`.
 *
 * Devolve lista com motivo, e não um booleano, porque a tela precisa dizer QUAL
 * cadastro e POR QUÊ: "já existe um cliente parecido" manda a pessoa procurar.
 */
export const clientesParecidos = (cliente, clients = []) => {
  const nome = normaliza(cliente?.name);
  const fone = soDigitos(cliente?.phone);
  if (!nome && !fone) return [];

  const meusTokens = significativas(cliente?.name);

  return (clients || [])
    .filter(outro => outro && outro.id !== cliente?.id)
    .map(outro => {
      if (nome && normaliza(outro.name) === nome) {
        return { client: outro, motivo: 'mesmo_nome' };
      }
      // Telefone só conta com pelo menos oito dígitos: "22" ou "9999" são
      // rascunho de quem estava digitando, não identidade.
      if (fone.length >= 8 && soDigitos(outro.phone) === fone) {
        return { client: outro, motivo: 'mesmo_telefone' };
      }
      const dele = significativas(outro.name);
      if (contido(meusTokens, dele) || contido(dele, meusTokens)) {
        return { client: outro, motivo: 'nome_contido' };
      }
      return null;
    })
    .filter(Boolean)
    // Nome igual primeiro: é o motivo que quase sempre é duplicata de verdade.
    .sort((a, b) => {
      const peso = { mesmo_nome: 0, mesmo_telefone: 1, nome_contido: 2 };
      return peso[a.motivo] - peso[b.motivo]
        || String(a.client.name || '').localeCompare(String(b.client.name || ''));
    });
};
